import { Controller } from "@hotwired/stimulus"
import { TurboSubmitStartEvent } from "@hotwired/turbo"
import { default as AudioRecorder, AudioRecorderEvent, isExpectedSetupError } from "../../audioRecorder"
import { Duration } from "luxon"

export default class VoiceEditorController extends Controller {
  declare audioEl: HTMLAudioElement
  declare audioContainerTarget: HTMLDivElement
  declare audioDisabledTarget: HTMLDivElement
  declare formTarget: HTMLFormElement
  declare recordAudioTarget: HTMLInputElement
  declare recordBtnTarget: HTMLButtonElement
  declare recordControlsTarget: HTMLDivElement
  declare recordingErrorTarget: HTMLDivElement
  declare hasRecordingErrorTarget: boolean
  declare stopBtnTarget: HTMLButtonElement
  declare timerTarget: HTMLDivElement
  declare timerContainerTarget: HTMLDivElement
  declare recordingFieldValue: string
  recorder: AudioRecorder | undefined
  recorderInitialized = false
  boundHandleSubmitStart: EventListener | undefined

  static targets = [
    "audioContainer",
    "audioDisabled",
    "form",
    "recordAudio",
    "recordBtn",
    "recordControls",
    "recordingError",
    "stopBtn",
    "timer",
    "timerContainer",
  ]

  static values = { recordingField: String }

  showAudioPlayer() {
    this.audioContainerTarget.parentElement?.classList.remove("d-none")
  }

  hideAudioPlayer() {
    this.audioContainerTarget.parentElement?.classList.add("d-none")
  }

  startRecording(recorder: AudioRecorder, microphone: Element) {
    microphone?.classList.toggle("pulse")
    this.timerContainerTarget.classList.toggle("d-none")
    if (this.hasRecordingErrorTarget) {
      this.recordingErrorTarget.classList.add("d-none")
    }
    this.hideAudioPlayer()
    this.toggleRecordButtons()

    recorder.start()
  }

  stopRecording(recorder: AudioRecorder, microphone: Element) {
    microphone?.classList.toggle("pulse")
    this.toggleRecordButtons()
    this.timerContainerTarget.classList.toggle("d-none")

    recorder.stop()

    this.showRerecord()
  }

  showRerecord() {
    const label = this.recordBtnTarget.querySelector<HTMLElement>(".voice-record-label")
    if (label) {
      label.textContent = "Re-record"
      this.timerTarget.textContent = "00:00"
    }
  }

  startRecordingAction() {
    if (!this.recorder) return
    const microphone = this.formTarget.getElementsByClassName("microphone-wrapper")[0]
    this.startRecording(this.recorder, microphone)
  }

  stopRecordingAction() {
    if (!this.recorder) return
    const microphone = this.formTarget.getElementsByClassName("microphone-wrapper")[0]
    this.stopRecording(this.recorder, microphone)
  }

  formTargetConnected() {
    this.audioEl = this.audioContainerTarget.getElementsByTagName("audio")[0]

    this.boundHandleSubmitStart = (event: Event) => {
      const turboEvent = event as TurboSubmitStartEvent
      if (this.recorder?.recording && this.recordAudioTarget.checked) {
        const formData = new FormData(this.formTarget)
        formData.set(this.recordingFieldValue, this.recorder.recording)
        turboEvent.detail.formSubmission.fetchRequest.body = formData
      }
    }
    this.formTarget.addEventListener("turbo:submit-start", this.boundHandleSubmitStart)

    if (this.recordAudioTarget.checked) {
      this.applyMode(this.recordAudioTarget)
    }
  }

  formTargetDisconnected(el: HTMLFormElement) {
    if (this.boundHandleSubmitStart) {
      el.removeEventListener("turbo:submit-start", this.boundHandleSubmitStart)
    }
  }

  handleModeChange(event: Event) {
    this.applyMode(event.currentTarget as HTMLInputElement)
  }

  async applyMode(mode: HTMLInputElement) {
    document.getElementById("text-to-speech-editor")?.classList.toggle("d-none")
    document.getElementById("record-audio-editor")?.classList.toggle("d-none")

    if (!this.recorderInitialized && mode.value == "record_audio") {
      const recorder = new AudioRecorder(this.audioEl)

      recorder.on("setupError", (event: AudioRecorderEvent) => {
        this.audioDisabledTarget.classList.remove("d-none")
        if (isExpectedSetupError(event.detail)) return
        // setTimeout escapes the promise chain so the error reaches window.onerror for Bugsnag
        setTimeout(() => {
          throw new Error("Could not setup audio recorder", { cause: event.detail })
        }, 0)
      }).on("setupComplete", () => {
        this.recorderInitialized = true
        this.recordControlsTarget.classList.toggle("d-none")
        if (this.audioEl.src) {
          this.recorder?.fixMetadata()
          this.showAudioPlayer()
          this.showRerecord()
        }
      }).on("recordingComplete", () => {
        this.showAudioPlayer()
      }).on("recordingProgress", (e: AudioRecorderEvent) => {
        if (typeof e.detail === "number") {
          this.timerTarget.textContent = Duration
            .fromMillis(e.detail)
            .toFormat("mm:ss")
        }
      })
      this.recorder = recorder
    }
  }

  toggleRecordButtons() {
    this.recordBtnTarget.classList.toggle("d-none")
    this.stopBtnTarget.classList.toggle("d-none")
  }
}
